import React, { useState } from 'react';
import { Form, message, Checkbox } from 'antd';
import { MailOutlined, LockOutlined, PhoneOutlined } from '@ant-design/icons';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../AuthProvider';
import './auth.css';
import AuthShell from '../../ui/AuthShell';
import Card from '../../ui/Card';
import AuthInput from '../../ui/AuthInput';
import Button from '../../ui/Button';
import AuthHeader from '../../ui/AuthHeader';
import Turnstile from '../../components/Turnstile';

const AuthSignUp = () => {
  const [form] = Form.useForm();
  const location = useLocation();
  const tenantType = 'employee';
  const pill = 'For Nurses & Medical Professionals';
  const subtitle = 'Join the VitalStaf network for flexible assignments, fast onboarding, and secure shifts.';
  const [loading, setLoading] = useState(false);
  const [cfToken, setCfToken] = useState(null);
  const { signup } = useAuth();

  const onFinish = async (values) => {
    setLoading(true);
    try {
      if (!cfToken) {
        message.error('Please verify the CAPTCHA.');
        setLoading(false);
        return;
      }

      if (values.phoneNumber) {
        localStorage.setItem('signupPhoneNumber', values.phoneNumber);
      }

      await signup(values.email, values.password, tenantType);
      message.success({ content: 'Account created! Check your email to verify.', duration: 4 });
      localStorage.setItem('fromSignup', 'true');
    } catch (error) {
      switch (error.code) {
        case 'auth/email-already-in-use':
          message.error('This email is already in use.');
          break;
        case 'auth/invalid-email':
          message.error('The email address is not valid.');
          break;
        case 'auth/weak-password':
          message.error('The password is too weak.');
          break;
        default:
          message.error('Failed to sign up. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const validateConfirmPassword = (_, value) => {
    if (value && value !== form.getFieldValue('password')) {
      return Promise.reject(new Error('The two passwords do not match!'));
    }
    return Promise.resolve();
  };

  const signinLink = '/auth/login';

  return (
    <AuthShell>
      <Card className="auth-form-wrapper" style={{ padding: 24 }}>
        <AuthHeader pillText={pill} title="Create your account" subtitle={subtitle} />

        <Form form={form} name="signup" onFinish={onFinish} layout="vertical" className="auth-form">
          <Form.Item
            name="email"
            label="Email address"
            rules={[
              { required: true, message: 'Please enter your email!' },
              { type: 'email', message: 'Please enter a valid email!' },
            ]}
          >
            <AuthInput icon={<MailOutlined />} placeholder="you@email.com" />
          </Form.Item>

          <Form.Item
            name="phoneNumber"
            label="Phone number"
            rules={[
              { required: true, message: 'Please enter your phone number!' },
            ]}
          >
            <AuthInput icon={<PhoneOutlined />} placeholder="Enter your phone number" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter your password!' },
              { min: 6, message: 'Password must be at least 6 characters long!' },
            ]}
          >
            <AuthInput icon={<LockOutlined />} type="password" placeholder="Enter your password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm password"
            dependencies={["password"]}
            rules={[
              { required: true, message: 'Please confirm your password!' },
              { validator: validateConfirmPassword },
            ]}
          >
            <AuthInput icon={<LockOutlined />} type="password" placeholder="Re-enter your password" />
          </Form.Item>


          <Form.Item name="smsConsent" valuePropName="checked" style={{ marginBottom: 20 }}>
            <Checkbox style={{ fontSize: 12 }}>
              OPTIONAL: By checking this box, you agree to receive SMS updates from Vitalstaf. Msg & data rates may apply. Msg frequency varies. Reply STOP to cancel at any time. Reply 'HELP' for assistance. See our <a href="https://vitalstaf.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</Checkbox>
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
            <Turnstile
              siteKey={"0x4AAAAAABt_D0jZYMl_CS2P"}
              action="signup"
              theme="light"
              onVerify={(t) => setCfToken(t)}
              onExpire={() => setCfToken(null)}
            />
          </div>

          <Form.Item>
            <Button
              type="submit"
              disabled={loading}
              className="vs-btn vs-btn-primary auth-form-button"
              style={{ width: '100%' }}
            >
              {loading ? 'Creating…' : 'Create account'}
            </Button>
          </Form.Item>

          <div style={{ marginTop: 4, marginBottom: 8, textAlign: 'center', fontSize: 12, color: '#666' }}>
            By creating an account, you agree to the
            <span> </span>
            <Link to="https://vitalstaf.com/terms">Terms</Link>
            <span> </span>
            and
            <span> </span>
            <Link to="https://vitalstaf.com/privacy">Privacy Policy</Link>.
          </div>
          <div className="auth-form-footer" style={{ textAlign: 'center', marginTop: 4 }}>
            Already have an account?{' '}
            <Link to={signinLink} className="auth-form-link">
              Sign in
            </Link>
          </div>
        </Form>
      </Card>
    </AuthShell>
  );
};

export default AuthSignUp;
